import type { LibraryConnection } from '@/lib/opds/types';
import { encodeFeedPath } from './url';

export interface Crumb {
  href: string;
  label: string;
  isCurrent: boolean;
  /** Absolute OPDS URL — null for the library home crumb. */
  absoluteUrl: string | null;
}

/**
 * Build a click-back breadcrumb chain rooted at the library home for an
 * OPDS feed URL. Walks parent paths via the v2 server's route-family
 * rules; labels prefer cached feed titles and fall back to the
 * URL-decoded last segment when no cache entry exists yet.
 */
export function buildBreadcrumbs(
  currentAbsoluteUrl: string,
  library: LibraryConnection,
  titles: Record<string, string>
): Crumb[] {
  let url: URL;
  try {
    url = new URL(currentAbsoluteUrl);
  } catch {
    return [];
  }

  const origin = url.origin;
  const search = url.search;
  const chainPaths: string[] = [];
  let p: string | null = stripTrailingSlash(url.pathname);
  let guard = 0;
  while (p && guard++ < 20) {
    chainPaths.push(p);
    p = parentPath(p);
  }
  chainPaths.reverse();

  const crumbs: Crumb[] = [
    {
      href: `/library/${library.id}`,
      label: library.name,
      isCurrent: false,
      absoluteUrl: null,
    },
  ];

  for (let i = 0; i < chainPaths.length; i++) {
    const path = chainPaths[i];
    const absolute = origin + path + search;
    const isCurrent = i === chainPaths.length - 1;
    crumbs.push({
      href: `/library/${library.id}/feed/${encodeFeedPath(absolute)}`,
      label: titles[absolute] ?? defaultLabel(path),
      isCurrent,
      absoluteUrl: absolute,
    });
  }

  return crumbs;
}

function stripTrailingSlash(p: string): string {
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

/**
 * Map an OPDS path to its parent path per the v2 server's route schema.
 * Returns null when the path is a top-level family entry whose parent is
 * the library root.
 */
function parentPath(path: string): string | null {
  if (path === '/' || path === '') return null;

  // Top-level family pages → parent is the library root (null).
  if (
    /^\/(authors|nicknames|sequences|psequences|names)index$/.test(path) ||
    path === '/genres' ||
    path === '/search'
  ) {
    return null;
  }

  // Single-letter pages: /<family>index/<L> → /<family>index
  const singleLetter = path.match(
    /^\/(authors|nicknames|sequences|psequences|names)index\/[^/]+$/
  );
  if (singleLetter) {
    return `/${singleLetter[1]}index`;
  }

  // Bucketed letter pages: /<N><family>index/<prefix> step up one level by
  // dropping the last code point of <prefix>. N==2 unwraps to /<family>index/<L>.
  const bucketed = path.match(
    /^\/(\d+)(authors|nicknames|sequences|psequences|names)index\/([^/]+)$/
  );
  if (bucketed) {
    const n = parseInt(bucketed[1], 10);
    const family = bucketed[2];
    let prefix: string;
    try {
      prefix = decodeURIComponent(bucketed[3]);
    } catch {
      prefix = bucketed[3];
    }
    const chars = Array.from(prefix);
    const parentPrefix = encodeURIComponent(chars.slice(0, n - 1).join(''));
    return n <= 2
      ? `/${family}index/${parentPrefix}`
      : `/${n - 1}${family}index/${parentPrefix}`;
  }

  // /author/<X>, /nickname/<X> → /authorsindex / /nicknamesindex
  const authorTop = path.match(/^\/(author|nickname)\/[^/]+$/);
  if (authorTop) {
    return `/${authorTop[1]}sindex`;
  }

  // /author/<X>/<wrapper>[/<seq>...]  — strip the last segment.
  if (/^\/(author|nickname)\/[^/]+\/.+$/.test(path)) {
    return path.slice(0, path.lastIndexOf('/'));
  }

  // /sequence/<X>, /psequence/<X> → corresponding family index
  const seqTop = path.match(/^\/(p?sequence)\/[^/]+$/);
  if (seqTop) {
    return `/${seqTop[1]}sindex`;
  }

  // /sequence/<X>/<nested>... — strip last segment
  if (/^\/(p?sequence)\/[^/]+\/.+$/.test(path)) {
    return path.slice(0, path.lastIndexOf('/'));
  }

  // /genres/<cat> → /genres
  if (/^\/genres\/[^/]+$/.test(path)) {
    return '/genres';
  }

  // /genre/<G> → /genres   (the cat isn't carried in the URL, so we skip it)
  if (/^\/genre\/[^/]+$/.test(path)) {
    return '/genres';
  }

  // /genre/<G>/author/<A>           → /genre/<G> (letters page; the specific
  //                                   letter the user came from isn't carried,
  //                                   matching how /author/<X> drops back to
  //                                   /authorsindex without remembering the bucket)
  // /genre/<G>/nickname/<N>          → /genre/<G>
  // /genre/<G>/(author|nickname)/.../<X> → strip last segment (wrapper / sequence
  //                                   drill stays inside the genre-scoped chain)
  const genreScopedAuthor = path.match(/^\/genre\/([^/]+)\/(?:author|nickname)\/([^/]+)$/);
  if (genreScopedAuthor) {
    return `/genre/${genreScopedAuthor[1]}`;
  }
  if (/^\/genre\/[^/]+\/(?:author|nickname)\/[^/]+\/.+$/.test(path)) {
    return path.slice(0, path.lastIndexOf('/'));
  }

  // /genre/<G>/<L> → /genre/<G>
  if (/^\/genre\/[^/]+\/[^/]+$/.test(path)) {
    return path.slice(0, path.lastIndexOf('/'));
  }

  // /work/<author>/<title> → /author/<author>
  const workScoped = path.match(/^\/work\/([^/]+)\/[^/]+$/);
  if (workScoped) {
    return `/author/${workScoped[1]}`;
  }

  // /work/<title>          → /namesindex
  if (/^\/work\/[^/]+$/.test(path)) {
    return '/namesindex';
  }

  return null;
}

function defaultLabel(path: string): string {
  const seg = path.split('/').filter(Boolean).pop() ?? '';
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}
